import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreditCard, Phone, RefreshCw, Ship } from "lucide-react";
import Seo from "../components/Seo";
import { createAccountPaymentSession, getAccountOrder, getTrips } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { money, prettyDate, time12 } from "../lib/format";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];
const PAGE_SIZE = 5;
const today = () => new Date().toISOString().slice(0, 10);

const when = (t) =>
  t.startDateTime
    ? `${prettyDate(t.startDateTime.slice(0, 10))} · ${time12(t.startDateTime.slice(11, 16))} – ${time12((t.endDateTime || "").slice(11, 16))}`
    : "";

/** Money comes from the vendor's own paymentSummary -- never computed here. */
const Money = ({ order, live, index }) => {
  const m = live || order;
  return (
    <div data-testid={`trip-money-${index + 1}`} className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-sand-border pt-4 text-sm">
      {typeof m.totalDue === "number" && (
        <span className="text-slate-600">
          Total <strong className="font-bold text-navy">{money(m.totalDue)}</strong>
        </span>
      )}
      {typeof m.paid === "number" && (
        <span className="text-slate-600">
          Paid <strong className="font-bold text-navy" data-testid={`trip-paid-${index + 1}`}>{money(m.paid)}</strong>
        </span>
      )}
      {typeof m.outstanding === "number" && (
        <span className="text-slate-600">
          Outstanding{" "}
          <strong
            data-testid={`trip-outstanding-${index + 1}`}
            className={m.outstanding > 0 ? "font-bold text-ember" : "font-bold text-navy"}
          >
            {money(m.outstanding)}
          </strong>
        </span>
      )}
      {typeof m.refunded === "number" && m.refunded > 0 && (
        <span className="text-slate-600">
          Refunded <strong className="font-bold text-navy">{money(m.refunded)}</strong>
        </span>
      )}
    </div>
  );
};

const OrderCard = ({ order, index }) => {
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState(null);
  const money_ = live || order;

  // Read with the customer's own session -- no confirmation code needed.
  const recheck = async () => {
    if (!order.bookingId) return;
    setBusy(true);
    try {
      setLive(await getAccountOrder(order.bookingId));
    } catch {
      setLive(null);
    }
    setBusy(false);
  };

  // The amount is the vendor's `outstanding`, never a figure we compute.
  const payBalance = async () => {
    setBusy(true);
    setPayError(null);
    try {
      const r = await createAccountPaymentSession(order.bookingId);
      if (r.checkoutUrl) {
        window.location.assign(r.checkoutUrl);
        return;
      }
      await recheck();
    } catch (err) {
      setPayError(err.payload?.userMessage || err.message);
    }
    setBusy(false);
  };

  return (
    <li data-testid={`trip-card-${index + 1}`} className="rounded-2xl border border-sand-border bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg font-bold text-navy" data-testid={`trip-label-${index + 1}`}>
            {order.lines.length === 1 ? order.lines[0].label : `${order.lineCount} boats`}
          </p>
          <p className="mt-1 text-sm text-slate-600">{when(order)}</p>
          {order.locationName && <p className="text-xs text-slate-500">{order.locationName}</p>}
        </div>
        {order.status && (
          <span data-testid={`trip-status-${index + 1}`} className="rounded-full bg-sand-card px-3 py-1 text-xs font-bold text-navy">
            {order.status}
          </span>
        )}
      </div>

      <div className="mt-4 rounded-xl bg-sand p-4 text-sm">
        {order.orderConfirmationCode ? (
          <p className="text-slate-600">
            Order code{" "}
            <code data-testid={`trip-order-code-${index + 1}`} className="font-bold text-navy">
              {order.orderConfirmationCode}
            </code>
          </p>
        ) : (
          <p data-testid={`trip-legacy-${index + 1}`} className="text-slate-600">
            An earlier single-boat reservation — it has its own code below.
          </p>
        )}
        <ul className="mt-3 space-y-2">
          {order.lines.map((l, i) => (
            <li key={l.reservationId ?? i} data-testid={`trip-line-${index + 1}-${i + 1}`} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span className="font-semibold text-navy">{l.label}</span>
              <span className="text-xs text-slate-600">
                {when(l)} · code{" "}
                <code data-testid={`trip-line-code-${index + 1}-${i + 1}`} className="font-bold text-navy">
                  {l.reservationConfirmationCode}
                </code>
                {typeof l.totalAmount === "number" && <> · {money(l.totalAmount)}</>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <Money order={order} live={live} index={index} />

      {order.bookingId && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          {(money_.outstanding || 0) > 0 && (
            <button
              type="button"
              data-testid={`trip-pay-balance-${index + 1}`}
              onClick={payBalance}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-ember px-5 py-2.5 text-xs font-bold text-white hover:bg-ember-hover disabled:opacity-60"
            >
              <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
              {busy ? "Starting…" : `Pay ${money(money_.outstanding)} balance`}
            </button>
          )}
          <button
            type="button"
            data-testid={`trip-recheck-${index + 1}`}
            onClick={recheck}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2 text-xs font-bold text-navy hover:bg-sand-card disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
            {busy ? "Checking…" : "Refresh balance"}
          </button>
        </div>
      )}

      {payError && (
        <p data-testid={`trip-pay-error-${index + 1}`} role="alert" className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {payError}
        </p>
      )}

      {/* No cancel button: the API still has no consumer cancellation endpoint. */}
      <p className="mt-4 text-xs text-slate-600">
        Need to change or cancel? Call us on{" "}
        <a href={WB.phoneHref} className="font-bold text-lake underline">{WB.phone}</a>. Cancellations can't be
        made online.
      </p>
    </li>
  );
};

export default function AccountTrips() {
  const { signedIn, ready } = useAuth();
  const [params] = useSearchParams();
  const returnedPaid = params.get("paid");
  const returnedCancelled = params.get("cancelled");
  const [state, setState] = useState({ loading: true, orders: [], total: 0, error: null });
  const [skip, setSkip] = useState(0);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    getTrips(skip, PAGE_SIZE)
      .then((d) =>
        setState({ loading: false, orders: d.orders || [], total: d.totalOrders || 0, error: null }),
      )
      .catch((err) =>
        setState({ loading: false, orders: [], total: 0, error: err.payload?.userMessage || err.message }),
      );
  }, [skip]);

  useEffect(() => {
    if (!ready || !signedIn) return;
    load();
  }, [ready, signedIn, load]);

  if (ready && !signedIn) {
    return (
      <div data-testid="trips-signed-out" className="mx-auto max-w-md px-5 py-32 text-center lg:px-10">
        <h1 className="font-display text-3xl font-extrabold text-navy">Please sign in</h1>
        <p className="mt-3 text-sm text-slate-600">
          Booked as a guest?{" "}
          <Link to="/booking/lookup" className="font-bold text-lake underline">Find my reservation</Link> instead.
        </p>
        <Link to="/account/login?next=/account/trips" data-testid="trips-login-link" className="mt-6 inline-block rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover">
          Sign in
        </Link>
      </div>
    );
  }

  const upcoming = state.orders.filter((o) => (o.startDateTime || "").slice(0, 10) >= today());
  const past = state.orders.filter((o) => (o.startDateTime || "").slice(0, 10) < today());
  const pages = Math.ceil(state.total / PAGE_SIZE) || 1;
  const page = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <>
      <Seo title="My trips | West Branch Marina" path="/account/trips" noindex />
      <div className="mx-auto max-w-3xl px-5 py-28 lg:px-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-navy">My trips</h1>
        <p className="mt-3 text-sm text-slate-600">
          Every boat on a trip is grouped under its order, with what you've paid and anything still due.
        </p>

        {/* Returning from the payment page proves nothing: the figures below are
            the vendor's, so they say whether the money actually arrived. */}
        {returnedPaid && (
          <p data-testid="trips-payment-returned" role="status" className="mt-6 rounded-xl border border-lake/40 bg-lake/[0.07] p-4 text-sm text-navy">
            Thanks — we've asked our payment provider to confirm. The paid and outstanding figures
            below come straight from our booking system; use “Refresh balance” if they haven't
            caught up yet.
          </p>
        )}
        {returnedCancelled && (
          <p data-testid="trips-payment-cancelled" role="status" className="mt-6 rounded-xl border border-sand-border bg-sand-card p-4 text-sm text-slate-700">
            No payment was taken and your reservation is still held. You can pay the balance any time
            below, or call the marina on {WB.phone}.
          </p>
        )}

        {state.loading && <p data-testid="trips-loading" className="mt-6 text-sm text-slate-600">Loading your trips…</p>}
        {state.error && (
          <p data-testid="trips-error" role="alert" className="mt-6 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {state.error}
          </p>
        )}

        {!state.loading && !state.error && state.orders.length === 0 && (
          <div data-testid="trips-empty" className="mt-8 rounded-2xl border border-sand-border bg-white p-8 text-center">
            <Ship className="mx-auto h-8 w-8 text-lake" aria-hidden="true" />
            <p className="mt-4 font-display text-xl font-bold text-navy">No trips yet</p>
            <p className="mt-2 text-sm text-slate-600">
              Bookings you make while signed in show up here with their codes and balances.
            </p>
            <Link to="/boat-rentals/west-branch" data-testid="trips-browse-link" className="mt-6 inline-block rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover">
              See the fleet
            </Link>
          </div>
        )}

        {upcoming.length > 0 && (
          <>
            <h2 className="mt-10 font-display text-xl font-bold text-navy">Upcoming</h2>
            <ul data-testid="trips-upcoming" className="mt-4 space-y-4">
              {upcoming.map((o, i) => <OrderCard key={o.orderKey ?? i} order={o} index={i} />)}
            </ul>
          </>
        )}

        {past.length > 0 && (
          <>
            <h2 className="mt-10 font-display text-xl font-bold text-navy">Past trips</h2>
            <ul data-testid="trips-past" className="mt-4 space-y-4 opacity-80">
              {past.map((o, i) => <OrderCard key={o.orderKey ?? i} order={o} index={upcoming.length + i} />)}
            </ul>
          </>
        )}

        {state.total > PAGE_SIZE && (
          <div data-testid="trips-pagination" className="mt-8 flex items-center justify-between gap-4 text-sm">
            <button
              type="button"
              data-testid="trips-prev"
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0}
              className="rounded-full border border-navy/20 px-5 py-2 font-bold text-navy hover:bg-sand-card disabled:opacity-40"
            >
              Previous
            </button>
            <span data-testid="trips-page-label" className="text-slate-600">
              Page {page} of {pages} · {state.total} orders
            </span>
            <button
              type="button"
              data-testid="trips-next"
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= state.total}
              className="rounded-full border border-navy/20 px-5 py-2 font-bold text-navy hover:bg-sand-card disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}

        <p className="mt-10 inline-flex items-center gap-2 text-sm text-slate-600">
          <Phone className="h-4 w-4 text-lake" aria-hidden="true" />
          Questions about a trip? Call {WB.phone}.
        </p>
      </div>
    </>
  );
}
